import React, { useContext, useRef, useState } from "react";
import axios from "axios";
import { useQueryClient } from "react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Assignment,
  Badge,
  Business,
  Category,
  ConfirmationNumber,
  Group,
  HowToReg,
  People,
  QrCodeScanner,
  SaveAlt,
  School,
  Visibility,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import BackBtn from "../../widgets/BackBtn";
import { ExportContext } from "../../context/ExportProvider";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT, USER_ROLES } from "../../utils/constants";
import { extractDate, getErrorResponse } from "../../utils/util";
import { getHeader } from "../../utils/utils2";
import OesExportDialog from "./OesExportDialog";
import { getCollectionRows, getServerPreviewPage, paginatePreviewRows, PREVIEW_PAGE_SIZE } from "../../utils/exportPreview";

const COLORS = {
  ink: "#172033",
  muted: "#6d7b91",
  border: "#e5eaf2",
};

const PREVIEW_LIMIT = PREVIEW_PAGE_SIZE;

const getNumericCount = (value) => {
  if (value == null || value === "") return undefined;
  const count = Number(value);
  return Number.isFinite(count) ? count : undefined;
};

const formatCount = (value) => getNumericCount(value)?.toLocaleString() ?? "—";

const useRoleCount = (role) =>
  useApiEp(`/admin/get-users-counter-by-role?role=${encodeURIComponent(role)}`);

const getStaffBadgeCount = (payload) => {
  if (payload == null) return undefined;
  if (getNumericCount(payload?.total) !== undefined) {
    return getNumericCount(payload.total);
  }

  return getCollectionRows(payload).reduce(
    (count, record) =>
      count + (Array.isArray(record?.badges) ? record.badges.length : 1),
    0
  );
};

const getCollectionCount = (payload) =>
  payload == null ? undefined : getCollectionRows(payload).length;

const getDisplayValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value !== "object") return String(value);
  if (value.name) return String(value.name);
  if (value.value != null) return getDisplayValue(value.value);
  return "—";
};

const getStaffBadgeRows = (payload) =>
  getCollectionRows(payload).flatMap((record) => {
    const badges = Array.isArray(record?.badges) ? record.badges : [record];

    return badges.map((badge) => ({
      ...badge,
      printType: badge?.printType || record?.printType,
      createdOn:
        badge?.createdOn ||
        badge?.printedOn ||
        record?.createdOn ||
        record?.printedOn,
    }));
  });

const SummaryCard = ({ title, count, loading, icon, accent, background }) => (
  <Card
    sx={{
      height: "100%",
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      boxShadow: "0 6px 18px rgba(20, 36, 66, 0.055)",
    }}
  >
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          display: "grid",
          placeItems: "center",
          borderRadius: 2,
          color: accent,
          bgcolor: background,
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      {loading ? (
        <Skeleton width="55%" height={38} />
      ) : (
        <Typography sx={{ color: COLORS.ink, fontSize: 26, fontWeight: 800 }}>
          {formatCount(count)}
        </Typography>
      )}
      <Typography sx={{ color: COLORS.muted, fontSize: 13, fontWeight: 650 }}>
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const analyticsCardSx = {
  height: "100%",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 3,
  boxShadow: "0 8px 24px rgba(20, 36, 66, 0.06)",
};

const chartTooltipStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  boxShadow: "0 10px 28px rgba(20, 36, 66, 0.12)",
};

const ModuleDistributionChart = ({ data, loading }) => (
  <Card sx={analyticsCardSx}>
    <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
      <Typography sx={{ color: COLORS.ink, fontSize: 18, fontWeight: 800 }}>
        Count Distribution by All Modules
      </Typography>
      <Typography sx={{ color: COLORS.muted, fontSize: 13, mt: 0.25, mb: 1 }}>
        Record volume available in every export module
      </Typography>
      {loading ? (
        <Skeleton variant="rounded" height={430} />
      ) : (
        <ResponsiveContainer width="100%" height={430}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 8, right: 48, bottom: 8, left: 18 }}
          >
            <CartesianGrid
              stroke="#e8edf5"
              strokeDasharray="4 5"
              horizontal={false}
            />
            <XAxis
              type="number"
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{ fill: COLORS.muted, fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="module"
              width={126}
              axisLine={false}
              tickLine={false}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <Tooltip
              contentStyle={chartTooltipStyle}
              formatter={(value) => [Number(value).toLocaleString(), "Records"]}
            />
            <Bar dataKey="count" name="Records" radius={[0, 6, 6, 0]} maxBarSize={24}>
              {data.map((item) => (
                <Cell key={item.module} fill={item.color} />
              ))}
              <LabelList
                dataKey="count"
                position="right"
                fill="#536176"
                fontSize={11}
                fontWeight={700}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

const AttendeeBreakdownChart = ({ data, loading }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card sx={analyticsCardSx}>
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Typography sx={{ color: COLORS.ink, fontSize: 18, fontWeight: 800 }}>
          Attendee Breakdown Share
        </Typography>
        <Typography sx={{ color: COLORS.muted, fontSize: 13, mt: 0.25, mb: 1 }}>
          Share of registrations across the main attendee groups
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" height={430} />
        ) : total === 0 ? (
          <Box
            sx={{
              height: 430,
              display: "grid",
              placeItems: "center",
              color: COLORS.muted,
            }}
          >
            No attendee data available.
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={430}>
            <PieChart>
              <Pie
                data={data.filter((item) => item.value > 0)}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="46%"
                innerRadius={72}
                outerRadius={125}
                paddingAngle={2}
                label={({ percent }) => `${Math.round(percent * 100)}%`}
              >
                {data
                  .filter((item) => item.value > 0)
                  .map((item) => (
                    <Cell key={item.name} fill={item.color} />
                  ))}
              </Pie>
              <Tooltip
                contentStyle={chartTooltipStyle}
                formatter={(value, name) => [
                  `${Number(value).toLocaleString()} (${Math.round(
                    (Number(value) / total) * 100
                  )}%)`,
                  name,
                ]}
              />
              <Legend
                iconType="circle"
                verticalAlign="bottom"
                wrapperStyle={{ color: COLORS.muted, fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const ExportDataPage = () => {
  const queryClient = useQueryClient();
  const previewRequest = useRef(0);
  const [oesDialogOpen, setOesDialogOpen] = useState(false);
  const [preview, setPreview] = useState({
    open: false,
    title: "",
    rows: [],
    total: undefined,
    loading: false,
    error: "",
    page: 0,
    pageCount: 0,
    pageSize: PREVIEW_LIMIT,
  });
  const {
    exportVisitors,
    visitorExporting,
    visitorExportType,
    exportExhibitors,
    exhibitorExporting,
    exportProducts,
    productExporting,
    oesExporting,
    exportTm,
    tmExporting,
    exportServiceCodes,
    serviceCodesExporting,
    exportStaffBadges,
    staffBadgesExporting,
    exportExhibitorLeads,
    exhibitorLeadsExporting,
  } = useContext(ExportContext);

  const visitors = useRoleCount(USER_ROLES.VISITOR);
  const students = useRoleCount(USER_ROLES.STUDENT);
  const tradeDelegates = useRoleCount(USER_ROLES.TRADE_DELEGATE);
  const specialInvitees = useRoleCount(USER_ROLES.SPECIAL_INVITEE);
  const exhibitors = useRoleCount(USER_ROLES.EXHIBITOR);
  const teamMembers = useRoleCount(USER_ROLES.TEAM);
  const products = useApiEp("/admin/count-products");
  const oesSubmits = useApiEp("/admin/count-oes-submits");
  const serviceCodes = useApiEp("/admin/service-codes?page=1&limit=1");
  const exhibitorLeads = useApiEp("/admin/get-exhibitors-scanned-wise");
  const staffBadges = useApiEp(
    "/admin/get-printed-staff-badges?page=1&limit=1"
  );

  const fetchPreviewData = async (path) => {
    const url = `${API_ENDPOINT}${path}`;
    return queryClient.fetchQuery([url], async () => {
      const { data } = await axios.get(url, getHeader());
      return data;
    }, { staleTime: 60000 });
  };

  const getVisitorPreview = async (role, page = 0, pageSize) => {
    const data = await fetchPreviewData(
      `/admin/get-visitors?type=${encodeURIComponent(
        role
      )}&page=${page + 1}&limit=${PREVIEW_LIMIT}&sq=&sourceType=`
    );

    return getServerPreviewPage(data, page, (user) => ({
        Name:
          [user.title, user.firstName, user.lastName]
            .filter(Boolean)
            .join(" ") || "—",
        Email: user.email || "—",
        Phone: user.mobileNo || "—",
        "Company Name": getDisplayValue(user.companyName),
        Designation: getDisplayValue(user.designation),
        City: getDisplayValue(user.city),
      }), undefined, pageSize);
  };

  const getExhibitorPreview = async (page = 0, pageSize) => {
    const data = await fetchPreviewData(`/user/get-exhibitors?page=${page + 1}&sq=`);

    return getServerPreviewPage(data, page, (exhibitor) => ({
        Company: exhibitor.companyName?.value || "—",
        Contact:
          [
            exhibitor.contactSalutation?.value,
            exhibitor.contactFirstName?.value,
            exhibitor.contactLastName?.value,
          ]
            .filter(Boolean)
            .join(" ") || "—",
        Email:
          exhibitor.contactEmail?.value || exhibitor.companyEmail?.value || "—",
        Hall: exhibitor.hallNumber?.value || "—",
        Stand: exhibitor.standNumber?.value || "—",
      }), exhibitors.data?.count, pageSize);
  };

  const getExhibitorLeadsPreview = async (page = 0) => {
    const [exhibitorsData, scannedData] = await Promise.all([
      fetchPreviewData("/admin/get-exhibitors-lookup-mini"),
      fetchPreviewData("/admin/get-exhibitors-scanned-wise"),
    ]);
    const companyLookup = new Map(
      getCollectionRows(exhibitorsData).map((exhibitor) => [
        exhibitor.username,
        typeof exhibitor.companyName === "string"
          ? exhibitor.companyName
          : exhibitor.companyName?.value,
      ])
    );

    const rows = getCollectionRows(scannedData)
      .map((item) => ({
        Username: item.username || "—",
        Company: companyLookup.get(item.username) || "Unknown company",
        "Leads Scanned": Number(item.count) || 0,
      }))
      .sort((a, b) => b["Leads Scanned"] - a["Leads Scanned"]);
    return paginatePreviewRows(rows, page);
  };

  const getServiceCodesPreview = async (page = 0, pageSize) => {
    const data = await fetchPreviewData(
      `/admin/service-codes?page=${page + 1}&limit=${PREVIEW_LIMIT}`
    );

    return getServerPreviewPage(data, page, (item) => ({
      "Exhibitor ID": item.exhibitorId || "—",
      Company: item.companyName || "—",
      Hall: item.hallNumber || "—",
      Stand: item.standNumber || "—",
    }), serviceCodes.data?.total, pageSize);
  };

  const getStaffBadgesPreview = async (page = 0) => {
    const badges = await queryClient.fetchQuery(["staff-badge-export-preview", API_ENDPOINT], async () => {
      const first = await fetchPreviewData("/admin/get-printed-staff-badges?page=1&limit=100");
      const totalPages = Number(first?.totalPages) || Math.ceil((Number(first?.total) || 0) / 100) || 1;
      const remaining = await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) =>
        fetchPreviewData(`/admin/get-printed-staff-badges?page=${index + 2}&limit=100`)
      ));
      return [first, ...remaining].flatMap(getStaffBadgeRows);
    }, { staleTime: 60000 });
    const rows = badges.map((badge) => ({
        "Barcode ID": badge.barcode_id || badge.barcodeId || "—",
        Name: badge.firstName || badge.name || "—",
        Company: badge.companyName || badge.company || "—",
        "Print Type": badge.printType || "—",
        "Printed On": extractDate(badge.createdOn) || "—",
      }));
    return paginatePreviewRows(rows, page);
  };

  const getProductsPreview = async (page = 0) => {
    const data = await fetchPreviewData("/admin/get-all-products");

    const rows = getCollectionRows(data).map((item) => ({
        Product: item.name || "—",
        Owner: item.owner || "—",
        "Assigned To": item.assignTo || "—",
        "Created On": extractDate(item.createdOn) || "—",
      }));
    return paginatePreviewRows(rows, page);
  };

  const getTeamMembersPreview = async (page = 0) => {
    const data = await fetchPreviewData("/admin/get-all-teammembers");

    const rows = getCollectionRows(data).map((item) => ({
        Name: [item.firstName, item.lastName].filter(Boolean).join(" ") || "—",
        Email: item.email || "—",
        Phone: item.phone || "—",
        Role: item.role || "—",
        Designation: item.designation || "—",
      }));
    return paginatePreviewRows(rows, page);
  };

  const handlePreview = async (item, page = 0) => {
    const requestId = ++previewRequest.current;
    const previousPageSize = preview.title === item.title ? preview.pageSize : undefined;
    setPreview({
      open: true,
      title: item.title,
      rows: [],
      total: getNumericCount(item.count),
      loading: true,
      error: "",
      page,
      pageCount: preview.title === item.title ? preview.pageCount : 0,
      pageSize: previousPageSize || PREVIEW_LIMIT,
    });

    try {
      const result = await item.getPreviewRows(page, previousPageSize);
      setPreview((current) =>
        requestId === previewRequest.current
          ? {
              ...current,
              ...result,
              loading: false,
            }
          : current
      );
    } catch (error) {
      setPreview((current) =>
        requestId === previewRequest.current
          ? {
              ...current,
              loading: false,
              error: getErrorResponse(error),
            }
          : current
      );
    }
  };

  const exportInProgress =
    visitorExporting ||
    exhibitorExporting ||
    productExporting ||
    oesExporting ||
    tmExporting ||
    serviceCodesExporting ||
    staffBadgesExporting ||
    exhibitorLeadsExporting;

  const exportItems = [
    {
      title: "Visitor List",
      description: "Download all registered visitor details in Excel format.",
      count: visitors.data?.count,
      countLoading: visitors.isLoading,
      icon: <People fontSize="medium" />,
      accent: "#1bad5b",
      background: "#ecfbf2",
      buttonColor: "success",
      exporting:
        visitorExporting && visitorExportType === USER_ROLES.VISITOR,
      onExport: () => exportVisitors(USER_ROLES.VISITOR),
      getPreviewRows: (page, pageSize) => getVisitorPreview(USER_ROLES.VISITOR, page, pageSize),
    },
    {
      title: "Student List",
      description: "Download registered student details in Excel format.",
      count: students.data?.count,
      countLoading: students.isLoading,
      icon: <School fontSize="medium" />,
      accent: "#0891b2",
      background: "#ecfeff",
      buttonColor: "info",
      exporting:
        visitorExporting && visitorExportType === USER_ROLES.STUDENT,
      onExport: () => exportVisitors(USER_ROLES.STUDENT),
      getPreviewRows: (page, pageSize) => getVisitorPreview(USER_ROLES.STUDENT, page, pageSize),
    },
    {
      title: "Trade Delegates",
      description: "Download approved trade delegate registration details.",
      count: tradeDelegates.data?.count,
      countLoading: tradeDelegates.isLoading,
      icon: <Group fontSize="medium" />,
      accent: "#b45309",
      background: "#fffbeb",
      buttonColor: "warning",
      exporting:
        visitorExporting &&
        visitorExportType === USER_ROLES.TRADE_DELEGATE,
      onExport: () => exportVisitors(USER_ROLES.TRADE_DELEGATE),
      getPreviewRows: (page, pageSize) => getVisitorPreview(USER_ROLES.TRADE_DELEGATE, page, pageSize),
    },
    {
      title: "Special Invitees",
      description: "Download special invitee registration details.",
      count: specialInvitees.data?.count,
      countLoading: specialInvitees.isLoading,
      icon: <HowToReg fontSize="medium" />,
      accent: "#9333ea",
      background: "#faf5ff",
      buttonColor: "secondary",
      exporting:
        visitorExporting &&
        visitorExportType === USER_ROLES.SPECIAL_INVITEE,
      onExport: () => exportVisitors(USER_ROLES.SPECIAL_INVITEE),
      getPreviewRows: (page, pageSize) => getVisitorPreview(USER_ROLES.SPECIAL_INVITEE, page, pageSize),
    },
    {
      title: "Exhibitor List",
      description: "Download exhibitor profiles and company information.",
      count: exhibitors.data?.count,
      countLoading: exhibitors.isLoading,
      icon: <Business fontSize="medium" />,
      accent: "#2f66ee",
      background: "#edf3ff",
      buttonColor: "primary",
      exporting: exhibitorExporting,
      onExport: exportExhibitors,
      getPreviewRows: getExhibitorPreview,
    },
    {
      title: "Exhibitor Leads",
      description: "Download the number of leads scanned by each exhibitor.",
      count: getCollectionCount(exhibitorLeads.data),
      countLoading: exhibitorLeads.isLoading,
      icon: <QrCodeScanner fontSize="medium" />,
      accent: "#2563eb",
      background: "#eff6ff",
      buttonColor: "primary",
      exporting: exhibitorLeadsExporting,
      onExport: exportExhibitorLeads,
      getPreviewRows: getExhibitorLeadsPreview,
    },
    {
      title: "Service Codes",
      description: "Download exhibitor service codes and stand information.",
      count: serviceCodes.data?.total,
      countLoading: serviceCodes.isLoading,
      icon: <ConfirmationNumber fontSize="medium" />,
      accent: "#0f766e",
      background: "#f0fdfa",
      buttonColor: "success",
      exporting: serviceCodesExporting,
      onExport: exportServiceCodes,
      getPreviewRows: getServiceCodesPreview,
    },
    {
      title: "Staff Badges",
      description: "Download the staff badges saved during badge printing.",
      count: getStaffBadgeCount(staffBadges.data),
      countLoading: staffBadges.isLoading,
      icon: <Badge fontSize="medium" />,
      accent: "#be123c",
      background: "#fff1f2",
      buttonColor: "error",
      exporting: staffBadgesExporting,
      onExport: exportStaffBadges,
      getPreviewRows: getStaffBadgesPreview,
    },
    {
      title: "OES Forms Data",
      description: "Choose a form and download its fields and tables in Excel.",
      count: oesSubmits.data?.count,
      countLoading: oesSubmits.isLoading,
      icon: <Assignment fontSize="medium" />,
      accent: "#7c3aed",
      background: "#f4edff",
      buttonColor: "secondary",
      exporting: oesExporting,
      onExport: () => setOesDialogOpen(true),
      onView: () => setOesDialogOpen(true),
    },
    {
      title: "Product List",
      description: "Download all uploaded products and their details.",
      count: products.data?.count,
      countLoading: products.isLoading,
      icon: <Category fontSize="medium" />,
      accent: "#e47800",
      background: "#fff6e5",
      buttonColor: "warning",
      exporting: productExporting,
      onExport: exportProducts,
      getPreviewRows: getProductsPreview,
    },
    {
      title: "Team Members List",
      description: "Download team member profiles and contact details.",
      count: teamMembers.data?.count,
      countLoading: teamMembers.isLoading,
      icon: <Group fontSize="medium" />,
      accent: "#0d8dcc",
      background: "#eaf7fd",
      buttonColor: "info",
      exporting: tmExporting,
      onExport: exportTm,
      getPreviewRows: getTeamMembersPreview,
    },
  ];

  const overviewItems = exportItems.map((item) => ({
    title: item.title,
    count: item.count,
    loading: item.countLoading,
    icon: item.icon,
    accent: item.accent,
    background: item.background,
  }));

  const moduleDistributionData = exportItems.map((item) => ({
    module: item.title,
    count: getNumericCount(item.count) || 0,
    color: item.accent,
  }));

  const attendeeBreakdownData = [
    {
      name: "Visitors",
      value: Number(visitors.data?.count) || 0,
      color: "#1bad5b",
    },
    {
      name: "Exhibitors",
      value: Number(exhibitors.data?.count) || 0,
      color: "#2f66ee",
    },
    {
      name: "Students",
      value: Number(students.data?.count) || 0,
      color: "#0891b2",
    },
    {
      name: "Trade Delegates",
      value: Number(tradeDelegates.data?.count) || 0,
      color: "#b45309",
    },
    {
      name: "Special Invitees",
      value: Number(specialInvitees.data?.count) || 0,
      color: "#9333ea",
    },
  ];

  const moduleCountsLoading = exportItems.some((item) => item.countLoading);
  const attendeeCountsLoading = [
    visitors,
    exhibitors,
    students,
    tradeDelegates,
    specialInvitees,
  ].some((query) => query.isLoading);

  const activePreviewItem = exportItems.find(
    (item) => item.title === preview.title
  );
  const visiblePreviewRows = preview.rows;
  const previewTotal = getNumericCount(preview.total) ?? preview.rows.length;
  const previewColumns = Array.from(new Set(visiblePreviewRows.flatMap(Object.keys)));

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#f7f9fc",
        mx: { xs: -1, sm: -2 },
        mt: -1,
        px: { xs: 2, sm: 3 },
        py: 2.5,
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <BackBtn />

        <Box
          sx={{
            mt: 2,
            mb: 3,
            p: { xs: 2.5, sm: 3.5 },
            color: "white",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
            background:
              "linear-gradient(135deg, #111a2d 0%, #17284a 48%, #2458d7 100%)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 230,
              height: 230,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.06)",
              right: -65,
              top: -120,
            }}
          />
          <Typography
            component="h1"
            sx={{
              position: "relative",
              fontSize: { xs: 25, sm: 30 },
              fontWeight: 800,
            }}
          >
            Export data
          </Typography>
          <Typography
            sx={{
              position: "relative",
              mt: 0.5,
              maxWidth: 620,
              color: "rgba(255,255,255,0.75)",
              fontSize: 14.5,
            }}
          >
            Choose a dataset below to download the latest records as an Excel
            file.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="data-overview-title">
          <Typography
            id="data-overview-title"
            sx={{ color: COLORS.ink, fontSize: 20, fontWeight: 800, mb: 1.5 }}
          >
            All module counts
          </Typography>
          <Grid container spacing={2} alignItems="stretch">
            {overviewItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={item.title}>
                <SummaryCard {...item} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box
          component="section"
          aria-labelledby="module-analytics-title"
          sx={{ mt: 4 }}
        >
          <Typography
            id="module-analytics-title"
            sx={{ color: COLORS.ink, fontSize: 20, fontWeight: 800, mb: 1.5 }}
          >
            Module analytics
          </Typography>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} lg={6}>
              <ModuleDistributionChart
                data={moduleDistributionData}
                loading={moduleCountsLoading}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AttendeeBreakdownChart
                data={attendeeBreakdownData}
                loading={attendeeCountsLoading}
              />
            </Grid>
          </Grid>
        </Box>

        <Typography
          sx={{
            color: COLORS.ink,
            fontSize: 20,
            fontWeight: 800,
            mt: 4,
            mb: 1.5,
          }}
        >
          Available exports
        </Typography>
        <Grid container spacing={2} alignItems="stretch">
          {exportItems.map((item) => (
            <Grid item xs={12} sm={6} lg={4} key={item.title}>
              <Card
                component="article"
                aria-label={item.title}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 2.5,
                  boxShadow: "0 6px 18px rgba(20, 36, 66, 0.055)",
                  transition: "transform 180ms ease, box-shadow 180ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 11px 26px rgba(20, 36, 66, 0.09)",
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        color: item.accent,
                        bgcolor: item.background,
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      {item.countLoading ? (
                        <Skeleton width={72} height={38} />
                      ) : (
                        <Typography
                          sx={{
                            color: COLORS.ink,
                            fontSize: 27,
                            lineHeight: 1.1,
                            fontWeight: 800,
                          }}
                        >
                          {formatCount(item.count)}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          color: COLORS.muted,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        records available
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    component="h2"
                    sx={{
                      mt: 2.25,
                      color: COLORS.ink,
                      fontSize: 17,
                      fontWeight: 750,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      color: COLORS.muted,
                      fontSize: 13.5,
                      lineHeight: 1.5,
                    }}
                  >
                    {item.description}
                  </Typography>
                </CardContent>

                <CardActions
                  sx={{
                    px: 2.5,
                    pb: 2.5,
                    pt: 0,
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1.2fr)",
                    gap: 1,
                  }}
                >
                  {!item.countLoading && getNumericCount(item.count) === 0 ? (
                    <Alert
                      severity="info"
                      sx={{ gridColumn: "1 / -1", py: 0, borderRadius: 2 }}
                    >
                      No items available to view or export.
                    </Alert>
                  ) : (
                    <>
                      <Button
                        fullWidth
                        variant="outlined"
                        color={item.buttonColor}
                        startIcon={<Visibility />}
                        onClick={() => item.onView ? item.onView() : handlePreview(item)}
                        sx={{
                          py: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                      >
                        View
                      </Button>
                      <LoadingButton
                        fullWidth
                        loading={item.exporting}
                        disabled={exportInProgress && !item.exporting}
                        variant="contained"
                        color={item.buttonColor}
                        startIcon={<SaveAlt />}
                        onClick={item.onExport}
                        sx={{
                          py: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          boxShadow: "none",
                        }}
                      >
                        {item.exporting ? "Exporting…" : "Export"}
                      </LoadingButton>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog
        open={preview.open}
        onClose={() =>
          setPreview((current) => ({ ...current, open: false }))
        }
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: COLORS.ink }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography component="span" sx={{ fontSize: 20, fontWeight: 800 }}>
              {preview.title} preview
            </Typography>
            <Chip
              color="primary"
              variant="outlined"
              label={`${formatCount(previewTotal)} total`}
              sx={{ fontWeight: 700 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {preview.loading ? (
            <Box sx={{ py: 1 }}>
              <Skeleton height={48} />
              <Skeleton height={48} />
              <Skeleton height={48} />
            </Box>
          ) : preview.error ? (
            <Alert severity="error">{preview.error}</Alert>
          ) : preview.rows.length === 0 ? (
            <Alert severity="info">
              No items are available to view or export.
            </Alert>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {`Showing ${preview.page * preview.pageSize + 1}–${preview.page * preview.pageSize + visiblePreviewRows.length} of ${formatCount(
                  previewTotal
                )} total records. Export all to download the complete dataset.`}
              </Typography>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2, maxHeight: 440 }}
              >
                <Table
                  stickyHeader
                  size="small"
                  aria-label={`${preview.title} preview`}
                >
                  <TableHead>
                    <TableRow>
                      {previewColumns.map((column) => (
                        <TableCell
                          key={column}
                          sx={{ fontWeight: 750, whiteSpace: "nowrap" }}
                        >
                          {column}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visiblePreviewRows.map((row, rowIndex) => (
                      <TableRow key={`${preview.title}-${rowIndex}`} hover>
                        {previewColumns.map((column) => (
                          <TableCell key={column} sx={{ minWidth: 130 }}>
                            {row[column] ?? "—"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {preview.pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination count={preview.pageCount} page={preview.page + 1} disabled={preview.loading} color="primary" onChange={(_, nextPage) => handlePreview(activePreviewItem, nextPage - 1)} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <LoadingButton
            variant="contained"
            color={activePreviewItem?.buttonColor || "primary"}
            startIcon={<SaveAlt />}
            loading={Boolean(activePreviewItem?.exporting)}
            disabled={
              !activePreviewItem ||
              previewTotal === 0 ||
              (exportInProgress && !activePreviewItem.exporting)
            }
            onClick={activePreviewItem?.onExport}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Export all
          </LoadingButton>
          <Button
            onClick={() =>
              setPreview((current) => ({ ...current, open: false }))
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <OesExportDialog open={oesDialogOpen} onClose={() => setOesDialogOpen(false)} exportInProgress={exportInProgress} />
    </Box>
  );
};

export default ExportDataPage;
